import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useFlow } from "../../hooks/useFlow";
import SurveyService from "../../lib/SurveyService";
import ExtendedWarrantyService from "../../lib/ExtendedWarrantyService";

import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyCard,
} from "./ExtendedWarrantyScreenWrapper";
import ExtendedWarrantyReadOnlySummary from "./ExtendedWarrantyReadOnlySummary";

import { ArrowLeft, Loader, Info, Lock } from "lucide-react";

const ExtendedWarrantySurveyScreen = ({ onComplete, onBack }) => {
  const { getIdToken, user } = useAuth();
  const { getSurveyId } = useFlow();

  // Survey ID from flow config
  const [surveyId] = useState(getSurveyId("main"));

  // Survey state
  const [surveyData, setSurveyData] = useState(null);
  const [sortedQuestions, setSortedQuestions] = useState([]);
  const [imgLoaded, setImgLoaded] = useState(false);

  // 🔥 PERSISTENCE: Initialize answers from sessionStorage
  const [answers, setAnswers] = useState(() => {
    const savedAnswers = sessionStorage.getItem("extendedWarrantySurveyData");
    return savedAnswers ? JSON.parse(savedAnswers) : {};
  });

  // 🔥 PERSISTENCE: Initialize current question from sessionStorage
  // const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
  //   const savedIndex = sessionStorage.getItem(
  //     "extendedWarrantySurveyQuestionIndex"
  //   );
  //   console.log("Restored question index from storage:", savedIndex);

  //   return savedIndex ? parseInt(savedIndex, 10) : 0;
  // });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // 🔥 PERSISTENCE: Add read-only state for completed surveys
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [previousResponses, setPreviousResponses] = useState(null);

  const handleReadOnlyContinue = () => {
    onComplete({
      surveyAnswers: answers,
      completedAt: previousResponses?.dateCompleted,
      readOnly: true,
      previousResponses: previousResponses?.responses,
      skipToExtendedWarrantyChoice: true,
    });
  };

  useEffect(() => {
    setImgLoaded(false);
  }, [currentQuestionIndex]);

  // 🔥 PERSISTENCE: Save answers to sessionStorage whenever they change
  useEffect(() => {
    if (Object.keys(answers).length > 0 && !isReadOnly) {
      sessionStorage.setItem(
        "extendedWarrantySurveyData",
        JSON.stringify(answers)
      );
    }
  }, [answers, isReadOnly]);

  // 🔥 PERSISTENCE: Save current question index to sessionStorage
  useEffect(() => {
    if (!isReadOnly) {
      sessionStorage.setItem(
        "extendedWarrantySurveyQuestionIndex",
        currentQuestionIndex.toString()
      );
    }
  }, [currentQuestionIndex, isReadOnly]);

  // 🔥 PERSISTENCE: Check for previous responses from API
  const checkPreviousResponses = async (token) => {
    try {
      if (!token) return { hasPreviousResponse: false };

      // Fetch previous responses using the same method as other surveys
      const responseData = await SurveyService.getSurveyResponsesBySurveyId(
        surveyId,
        token
      );

      // If we have completed responses
      if (responseData && responseData.dateCompleted) {
        setPreviousResponses(responseData);
        return { hasPreviousResponse: true, previousResponses: responseData };
      }

      return { hasPreviousResponse: false };
    } catch (err) {
      console.log("No previous responses found or error checking:", err);
      return { hasPreviousResponse: false };
    }
  };

  // 🔥 PERSISTENCE: Convert server response format to component answers format
  const convertResponsesFormatToAnswers = (responseData, surveyTemplate) => {
    if (!responseData || !responseData.responses || !surveyTemplate) {
      return {};
    }

    const convertedAnswers = {};

    responseData.responses.forEach((response) => {
      // Find the corresponding question in the survey template
      const question = surveyTemplate.questions.find(
        (q) => q.id === response.questionId
      );
      if (!question) return;

      const questionId = response.questionId;

      switch (response.responseType) {
        case "TEXT":
          convertedAnswers[questionId] = {
            value: response.answer || "",
            questionId,
          };
          break;

        case "CHOICE":
          if (question.multiSelect) {
            // Multi-select question
            const values = response.selections.map(
              (s) => s.optionValue || s.value
            );
            const answerIds = response.selections.map(
              (s) => s.choiceOptionId || s.id
            );

            convertedAnswers[questionId] = {
              values,
              answerIds,
              questionId,
              multiSelect: true,
            };
          } else {
            // Single-select question
            if (response.selections && response.selections.length > 0) {
              convertedAnswers[questionId] = {
                value:
                  response.selections[0].optionValue ||
                  response.selections[0].value,
                answerId:
                  response.selections[0].choiceOptionId ||
                  response.selections[0].id,
                questionId,
                multiSelect: false,
              };
            }
          }
          break;

        default:
          console.warn(`Unsupported response type: ${response.responseType}`);
      }
    });

    return convertedAnswers;
  };

  // Fetch survey data on mount
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setIsLoading(true);
        // Get Firebase token (if user is authenticated)
        let token = null;
        let previousResponses = { hasPreviousResponse: false };

        try {
          token = await getIdToken();
          if (user && token) {
            previousResponses = await checkPreviousResponses(token);
            // Set read-only mode if we have previous responses
            if (previousResponses.hasPreviousResponse) {
              setIsReadOnly(true);
            }
          }
        } catch (e) {
          console.warn("No authentication token available:", e);
        }

        // Fetch survey template
        const survey = await SurveyService.getSurveyById(surveyId, token);

        // Sort questions by displayOrder
        const questions = [...survey.questions].sort(
          (a, b) => a.displayOrder - b.displayOrder
        );

        setSurveyData(survey);
        setSortedQuestions(questions);

        // 🔥 PERSISTENCE: If we have previous responses, map them to current format
        if (
          previousResponses.hasPreviousResponse &&
          previousResponses.previousResponses &&
          survey
        ) {
          const convertedAnswers = convertResponsesFormatToAnswers(
            previousResponses.previousResponses,
            survey
          );
          setAnswers(convertedAnswers);
        }
        await createFlow();
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading Extended Warranty survey:", err);
        setError("Failed to load survey. Please try again.");
        setIsLoading(false);
      }
    };

    const createFlow = async () => {
      try {
        if (user) {
          console.log("qualifieds");
          const token = await getIdToken();
          console.log(token);
          if (!token) throw new Error("Authentication required");
          const flowData = await ExtendedWarrantyService.createFlow(token);
          sessionStorage.setItem("extendedWarrantyFlowId", flowData.id);
        }
      } catch (err) {
        console.error("Error loading Extended Warranty flow:", err);
      }
    };
    if (surveyId) {
      fetchSurvey();
    }
  }, [surveyId, getIdToken, user]);

  const handleAnswer = (questionId, value, answerId) => {
    // 🔥 PERSISTENCE: Don't allow changes in read-only mode
    if (isReadOnly) return;

    const currentQuestion = sortedQuestions[currentQuestionIndex];

    if (currentQuestion.multiSelect) {
      // Handle multiple selection
      const currentAnswers = answers[questionId] || {
        values: [],
        answerIds: [],
      };
      const isSelected = currentAnswers.values.includes(value);

      let newValues, newAnswerIds;
      if (isSelected) {
        // Remove selection
        newValues = currentAnswers.values.filter((v) => v !== value);
        newAnswerIds = currentAnswers.answerIds.filter((id) => id !== answerId);
      } else {
        // Add selection
        newValues = [...currentAnswers.values, value];
        newAnswerIds = [...currentAnswers.answerIds, answerId];
      }

      const newAnswers = {
        ...answers,
        [questionId]: {
          values: newValues,
          answerIds: newAnswerIds,
          questionId,
          multiSelect: true,
        },
      };
      setAnswers(newAnswers);

      // Don't auto-advance for multi-select questions
    } else {
      // Handle single selection
      const newAnswers = {
        ...answers,
        [questionId]: {
          value,
          answerId,
          questionId,
          multiSelect: false,
        },
      };
      setAnswers(newAnswers);

      // No auto-advance - user must click Next button
    }
  };

  const moveToNextQuestion = (currentQuestion, selectedValue) => {

console.log("Someone called move to next question");

    // 🔥 PERSISTENCE: Don't allow navigation in read-only mode unless going to completion
    if (isReadOnly && currentQuestionIndex < sortedQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      return;
    }

    // Check if current question has conditional logic
    const selectedOption = currentQuestion.options?.find(
      (option) => option.value === selectedValue
    );
    console.log("selected option == " + selectedOption);
    if (selectedOption?.nextQuestionId) {
      console.log(
        "selected option next question == " + selectedOption?.nextQuestionId
      );
      // Find the target question by ID
      const targetQuestionIndex = sortedQuestions.findIndex(
        (q) => q.id === selectedOption.nextQuestionId
      );
      console.log("selected targetQuestionIndex == " + targetQuestionIndex);
      if (targetQuestionIndex !== -1) {
        setCurrentQuestionIndex(targetQuestionIndex);
        return;
      }
    }

    // Default behavior: move to next question in order
    if (currentQuestionIndex < sortedQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      // Survey completed
      handleSurveyComplete();
    }
  };

  const handleSurveyComplete = async () => {
    // 🔥 PERSISTENCE: In read-only mode, navigate to vehicle choices
    if (isReadOnly) {
      onComplete({
        surveyAnswers: answers,
        completedAt: previousResponses.dateCompleted,
        readOnly: true,
        previousResponses: previousResponses.responses,
        // Signal that this should go to extended warranty choice screen
        skipToExtendedWarrantyChoice: true,
      });
      return;
    }

    try {
      console.log("set is submitting");
      // Format responses for API submission
      const formattedResponses = Object.entries(answers)
        .map(([questionId, answer]) => {
          const question = sortedQuestions.find((q) => q.id === questionId);

          if (question.type === "CHOICE") {
            if (answer.multiSelect) {
              return {
                type: "CHOICE",
                questionId,
                selections: answer.values.map((value, index) => ({
                  choiceOptionId: answer.answerIds[index],
                  value: value,
                })),
              };
            } else {
              return {
                type: "CHOICE",
                questionId,
                selections: [
                  {
                    choiceOptionId: answer.answerId,
                    value: answer.value,
                  },
                ],
              };
            }
          }

          return {
            type: "TEXT",
            questionId,
            answer: answer.value || "",
          };
        })
        .filter(Boolean);

      // Submit to API if user is authenticated
      if (user) {
        const token = await getIdToken();
        await SurveyService.submitSurveyResponsesForExtendedWarranty(
          surveyId,
          user.uid,
          formattedResponses,
          token
        );

        // 🔥 PERSISTENCE: Mark survey as submitted and clear session storage
        sessionStorage.setItem("extendedWarrantySurveySubmitted", "true");
        // Keep the data for now, don't clear it immediately
      }

      // Call completion handler with survey answers for flow analysis
      onComplete({
        surveyAnswers: answers,
        formattedResponses,
        completedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error submitting Extended Warranty survey:", err);
      setError("Failed to submit survey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = () => {
    if (currentQuestionIndex >= sortedQuestions.length) {
      return null;
    }

    const question = sortedQuestions[currentQuestionIndex];
    if (question.type === "INSTRUCTION") {
      return (
        <div className="space-y-4">
          {/* Show image if mediaType is IMAGE */}
          {question.mediaType === "IMAGE" && question.mediaUrl && (
            <div className="flex justify-center">
              {/* Loading spinner - shown until image loads */}
              {!imgLoaded && (
                <div className="flex flex-col items-center justify-center p-8">
                  <Loader className="w-8 h-8 animate-spin text-purple-600 mb-2" />
                  <span className="text-sm text-gray-600">
                    Loading image...
                  </span>
                </div>
              )}

              {/* Image - hidden until loaded */}
              <img
                src={question.mediaUrl}
                alt="Instruction visual"
                className={`max-w-full h-auto rounded-lg shadow-sm ${
                  imgLoaded ? "block" : "hidden"
                }`}
                onLoad={() => setImgLoaded(true)}
                onError={(e) => {
                  console.error(
                    "Failed to load instruction image:",
                    question.mediaUrl
                  );
                  setImgLoaded(true); // Hide loader even if image fails
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          {/* Show instruction text if no image or if image loading fails */}
          {(!question.mediaType || question.mediaType !== "IMAGE") &&
            question.instructionText && (
              <div className="prose prose-sm max-w-none">
                <div
                  className="text-center"
                  dangerouslySetInnerHTML={{
                    __html: question.instructionText.replace(/\n/g, "<br/>"),
                  }}
                />
              </div>
            )}

          {/* Action buttons */}
          <div className="flex flex-col gap-3">
            {/* Primary button */}
            {question.clickTextPrimary && (
              <button
                onClick={() => {
                  if (question.clickUrlPrimary === "getOffers") {
                    setIsSubmitting(true);

                    handleSurveyComplete();
                  } else if (question.clickUrlPrimary === "ownliOffers") {
                    window.open("https://www.ownli.co", "_blank");
                  } else {
                    // Handle other primary actions
                    moveToNextQuestion(question);
                  }
                }}
                disabled={isSubmitting}
                className={`w-full flex uppercase font-bold items-center justify-center gap-2 text-white px-4 py-3 rounded-lg transition-colors ${
                  isSubmitting
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-purpleGradient hover:bg-purpleGradientDark"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>{question.clickTextPrimary}</span>
                )}
              </button>
            )}

            {/* Secondary link */}
            {question.clickTextSecondary && (
              <button
                onClick={() => {
                  if (question.clickUrlSecondary === "ownliOffers") {
                    window.open("https://www.ownli.co", "_blank");
                  } else {
                    // Handle other secondary actions
                    moveToNextQuestion(question);
                  }
                }}
                className="text-center text-gray-600 hover:text-gray-900 text-sm transition-colors"
              >
                {question.clickTextSecondary}
              </button>
            )}

            {/* Fallback continue button if no custom buttons */}
            {!question.clickTextPrimary && !question.clickTextSecondary && (
              <button
                onClick={() => moveToNextQuestion(question)}
                disabled={isReadOnly}
                className={`w-full flex uppercase font-bold items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                  isReadOnly
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-purpleGradient text-white hover:bg-purpleGradientDark"
                }`}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      );
    }

    if (question.type === "CHOICE") {
      return (
        <ExtendedWarrantyCard>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                {question.prompt}
              </h2>
              {question.helpText && (
                <p className="text-sm text-gray-600">{question.helpText}</p>
              )}
            </div>

            <div className="space-y-3">
              {question.options
                ?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                .map((option) => {
                  const isSelected = question.multiSelect
                    ? answers[question.id]?.values?.includes(option.value)
                    : answers[question.id]?.value === option.value;

                  return (
                    <button
                      key={option.id}
                      onClick={() =>
                        handleAnswer(question.id, option.value, option.id)
                      }
                      disabled={isReadOnly}
                      className={`w-full p-4 text-left rounded-lg border transition-all ${
                        isSelected
                          ? "border-purple-500 bg-purple-50 text-purple-900"
                          : isReadOnly
                          ? "border-gray-200 bg-gray-50 text-gray-700 cursor-not-allowed"
                          : "border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {question.multiSelect && (
                          <div
                            className={`w-4 h-4 rounded border-2 transition-colors ${
                              isSelected
                                ? "bg-purple-500 border-purple-500"
                                : "border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                        )}
                        <span>{option.text}</span>
                      </div>
                    </button>
                  );
                })}
            </div>

            {/* Next button for single-select choice questions */}
            {!question.multiSelect && (
              <button
                onClick={() =>
                  moveToNextQuestion(question, answers[question.id]?.value)
                }
                disabled={
                  (!answers[question.id]?.value && !isReadOnly) || isSubmitting
                }
                className={`w-full flex uppercase font-bold items-center justify-center gap-2 text-white px-4 py-3 rounded-lg transition-colors mt-4 ${
                  (answers[question.id]?.value || isReadOnly) && !isSubmitting
                    ? "bg-purpleGradient hover:bg-purpleGradientDark"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : currentQuestionIndex === sortedQuestions.length - 1 ? (
                  isReadOnly ? (
                    "Continue to Vehicle Options"
                  ) : (
                    "Complete"
                  )
                ) : (
                  "Next"
                )}
              </button>
            )}

            {/* Next button for multi-select questions */}
            {question.multiSelect && (
              <button
                onClick={() => moveToNextQuestion(question)}
                disabled={isSubmitting}
                className={`w-full flex uppercase font-bold items-center justify-center gap-2 text-white px-4 py-3 rounded-lg transition-colors mt-4 ${
                  isSubmitting
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-purpleGradient hover:bg-purpleGradientDark"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Next"
                )}
              </button>
            )}
          </div>
        </ExtendedWarrantyCard>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <ExtendedWarrantyScreenWrapper>
        <ExtendedWarrantyCard>
          <div className="flex flex-col items-center justify-center p-8">
            <Loader className="w-8 h-8 animate-spin text-purple-600 mb-4" />
            <span className="text-gray-600">Loading survey...</span>
          </div>
        </ExtendedWarrantyCard>
      </ExtendedWarrantyScreenWrapper>
    );
  }

  if (error) {
    return (
      <ExtendedWarrantyScreenWrapper>
        <ExtendedWarrantyCard variant="error">
          <div className="text-center">
            <p className="text-red-700 font-medium mb-2">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-red-600 hover:text-red-800 text-sm"
            >
              Try Again
            </button>
          </div>
        </ExtendedWarrantyCard>
      </ExtendedWarrantyScreenWrapper>
    );
  }

  return (
    <ExtendedWarrantyScreenWrapper>
      {/* Back button */}
      {/* {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )} */}

      {/* 🔥 EXTRACTED: Show summary in read-only mode, otherwise show normal question flow */}
      {isReadOnly ? (
        <>
          {/* Read-only banner */}
          <ExtendedWarrantyCard variant="info">
            <div className="flex">
              <div className="flex-shrink-0">
                <Lock className="h-5 w-5 text-blue-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  This survey was completed on{" "}
                  {new Date(
                    previousResponses.dateCompleted
                  ).toLocaleDateString()}
                  . Review your responses below.
                </p>
              </div>
            </div>
          </ExtendedWarrantyCard>

          {/* Show extracted summary component */}
          <ExtendedWarrantyReadOnlySummary
            sortedQuestions={sortedQuestions}
            answers={answers}
            previousResponses={previousResponses}
            onContinue={handleReadOnlyContinue}
          />
        </>
      ) : (
        // Show normal question flow for new surveys
        renderQuestion()
      )}

      {isSubmitting && (
        <ExtendedWarrantyCard>
          <div className="flex items-center justify-center p-4">
            <Loader className="w-5 h-5 animate-spin text-purple-600 mr-2" />
            <span className="text-gray-600">Submitting your responses...</span>
          </div>
        </ExtendedWarrantyCard>
      )}
    </ExtendedWarrantyScreenWrapper>
  );
};

export default ExtendedWarrantySurveyScreen;
